import { state, clipDurationSeconds, hasAnimation } from '../state.ts';

/** One timeline lane (a contact pin, an actor's offset layer, …). */
export interface Lane {
  id: string;
  label: string;
  color: number;
  /** undefined = no on/off chip. */
  enabled?: boolean;
  /** Envelope blocks in seconds; opacity 0..1 conveys strength. */
  blocks: { start: number; end: number; opacity: number }[];
  /** Key times in seconds (rendered as draggable diamonds). */
  keys: number[];
  onToggle?: () => void;
  /** Click a diamond: select the owner + scrub to the key. */
  onSelectKey: (index: number) => void;
  /** Drag a diamond to retime (snapping/clamping owned by main). */
  onMoveKey: (index: number, seconds: number) => void;
  onDeleteKey?: (index: number) => void;
}

export interface TimelineTracksDeps {
  getLanes: () => Lane[];
}

/**
 * Generic multi-track strip under the camera strip: one lane per contact pin /
 * adjustment layer, with envelope blocks (opacity = strength), draggable key
 * diamonds and an on/off chip. Same percent-vs-clip-duration positioning and
 * drag patterns as cameraStrip.ts; scrolls vertically past ~3 lanes.
 */
export function createTimelineTracks(deps: TimelineTracksDeps) {
  const container = byId<HTMLDivElement>('timeline-tracks');
  const playhead = document.createElement('div');
  playhead.className = 'strip-playhead';
  container.appendChild(playhead);

  let selected: { laneId: string; index: number } | null = null;
  let dragging = false;
  let lastSignature = '';
  let laneEls: { el: HTMLDivElement; lane: Lane; keyEls: HTMLDivElement[] }[] = [];

  function rebuild(lanes: Lane[]): void {
    for (const l of laneEls) l.el.remove();
    laneEls = [];
    const dur = Math.max(clipDurationSeconds(), 1e-6);

    for (const lane of lanes) {
      const el = document.createElement('div');
      el.className = 'track-lane';
      el.classList.toggle('disabled', lane.enabled === false);
      const rgb = `${(lane.color >> 16) & 255}, ${(lane.color >> 8) & 255}, ${lane.color & 255}`;

      for (const block of lane.blocks) {
        const b = document.createElement('div');
        b.className = 'track-block';
        b.style.left = `${(block.start / dur) * 100}%`;
        b.style.width = `${Math.max(0, ((block.end - block.start) / dur) * 100)}%`;
        b.style.background = `rgba(${rgb}, ${(0.12 + 0.55 * Math.min(block.opacity, 1)).toFixed(3)})`;
        el.appendChild(b);
      }

      const keyEls: HTMLDivElement[] = [];
      lane.keys.forEach((time, index) => {
        const k = document.createElement('div');
        k.className = 'strip-key';
        k.classList.toggle(
          'selected',
          selected?.laneId === lane.id && selected.index === index
        );
        k.style.left = `${(time / dur) * 100}%`;
        k.title = `${time.toFixed(2)}s — drag to retime`;
        attachKeyDrag(k, lane, index);
        el.appendChild(k);
        keyEls.push(k);
      });

      // Floating chip: label + optional on/off toggle (keeps percent alignment).
      const chip = document.createElement('div');
      chip.className = 'track-chip';
      if (lane.onToggle) {
        const toggle = document.createElement('button');
        toggle.type = 'button';
        toggle.className = 'track-toggle';
        toggle.textContent = lane.enabled === false ? '○' : '●';
        toggle.title = lane.enabled === false ? 'Turn this pin on' : 'Turn this pin off';
        toggle.style.color = `rgb(${rgb})`;
        toggle.addEventListener('click', (e) => {
          e.stopPropagation();
          lane.onToggle!();
        });
        chip.appendChild(toggle);
      }
      const label = document.createElement('span');
      label.textContent = lane.label;
      chip.appendChild(label);
      el.appendChild(chip);

      container.appendChild(el);
      laneEls.push({ el, lane, keyEls });
    }
    container.appendChild(playhead); // keep the playhead on top
  }

  function attachKeyDrag(el: HTMLDivElement, lane: Lane, index: number): void {
    el.addEventListener('pointerdown', (e) => {
      e.preventDefault();
      e.stopPropagation();
      dragging = true;
      try {
        el.setPointerCapture(e.pointerId);
      } catch {
        /* synthetic events may lack a real pointer */
      }
      const rect = container.getBoundingClientRect();
      let movedPx = 0;
      const startX = e.clientX;
      const move = (ev: PointerEvent): void => {
        movedPx = Math.max(movedPx, Math.abs(ev.clientX - startX));
        if (movedPx < 3) return;
        const frac = Math.min(Math.max((ev.clientX - rect.left) / rect.width, 0), 1);
        lane.onMoveKey(index, frac * clipDurationSeconds());
      };
      const up = (): void => {
        dragging = false;
        el.removeEventListener('pointermove', move);
        el.removeEventListener('pointerup', up);
        selected = { laneId: lane.id, index };
        if (movedPx < 3) lane.onSelectKey(index); // it was a click
        lastSignature = '';
        update();
      };
      el.addEventListener('pointermove', move);
      el.addEventListener('pointerup', up);
    });
  }

  /** Reposition existing diamonds while one is being dragged (no rebuild). */
  function reposition(lanes: Lane[]): void {
    const dur = Math.max(clipDurationSeconds(), 1e-6);
    for (const entry of laneEls) {
      const lane = lanes.find((l) => l.id === entry.lane.id);
      if (!lane) continue;
      entry.keyEls.forEach((k, i) => {
        if (lane.keys[i] !== undefined) k.style.left = `${(lane.keys[i] / dur) * 100}%`;
      });
    }
  }

  /** Delete the selected lane key (Delete key). True when something happened. */
  function deleteSelected(): boolean {
    if (!selected) return false;
    const entry = laneEls.find((l) => l.lane.id === selected!.laneId);
    if (!entry?.lane.onDeleteKey) {
      selected = null;
      return false;
    }
    const index = selected.index;
    selected = null;
    lastSignature = '';
    entry.lane.onDeleteKey(index);
    return true;
  }

  /** Reflect state (called on every notify()). */
  function update(): void {
    const lanes = deps.getLanes();
    container.hidden = !hasAnimation() || lanes.length === 0;
    if (container.hidden) return;

    const pct = state.totalFrames > 0 ? (state.currentFrame / state.totalFrames) * 100 : 0;
    playhead.style.left = `${pct}%`;

    if (dragging) {
      reposition(lanes);
      return;
    }
    if (selected && !lanes.some((l) => l.id === selected!.laneId)) selected = null;
    const signature = JSON.stringify([
      lanes.map((l) => [l.id, l.label, l.color, l.enabled, l.blocks, l.keys]),
      selected,
      state.totalFrames,
    ]);
    if (signature !== lastSignature) {
      lastSignature = signature;
      rebuild(lanes);
    }
  }

  return { update, deleteSelected };
}

function byId<T extends HTMLElement>(id: string): T {
  const el = document.getElementById(id);
  if (!el) throw new Error(`Missing element #${id}`);
  return el as T;
}
